import { HttpClient } from "@angular/common/http";
import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { CancelEvent, EditEvent, GridComponent, SaveEvent } from "@progress/kendo-angular-grid";
import { forkJoin } from "rxjs";
import { finalize, take } from "rxjs/operators";
import { DestroyBase } from "../../../../components/common/ng-destroy-base";
import { AppServiceHelperService } from "../../../../services/app-service-helper.service";
import { FileDownloadService } from "../../../../services/file-download-service.service";
import { environment } from "../../../../../environments/environment";
import { EventBusService } from "../../../../services/event-bus.service";
import { ObjectUtil } from "../../../../components/common/object-util/object-util";
import { RxjsHelper } from "../../../../components/common/utils/rxjs-helper";

/**
 * User administration
 */
@Component({
  selector: 'admin-user-management',
  templateUrl: './admin-user-management.component.html',
  styleUrls: ['./admin-user-management.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AdminUserManagementComponent extends DestroyBase implements OnInit {

  /**
   * The workplace
   */
  private _workplace: any;

  /**
   * Sets the workplace
   */
  @Input() public set workplace(workplace: any) {
    this._workplace = workplace;
  }

  /**
   * Indicator if data is loading
   */
  loading = false;

  /**
   * Hide user ACD name (for local WinAuth only)
   */
  hideUserAcdNameColumn = false;

  /**
   * Possible page sizes
   */
  pageSizes = [10, 25];

  /**
   * Users
   */
  users: any[];

  /**
   * Available business units
   */
  availableBUsReal: any[] = [];

  /**
   * Available role BUs
   */
  availableBUsRole: any[] = [];

  /**
   * Available roles
   */
  availableRoles: any[] = [];

  /**
   * Available business lines
   */
  availableBusinessLineNr: any[] = [];

  /**
   * Available sectors
   */
  availableSectors: any[] = [];

  /**
   * Available regions
   */
  availableRegions: any[] = [];

  /**
   * Available organization roles
   */
  availableOrganizationalRoles: any[] = [];

  /**
    * Form group used for adding/editing grid row
    */
  public formGroup: UntypedFormGroup;

  /**
  * Edited row index
  */
  private editedRowIndex: number;

  /**
    * Kendo grid component
    */
  @ViewChild("kendoGrid") kendoGrid: GridComponent;

  /**
   * Show the ASA dialog?
   */
  public showAsaDialog = false;

  /**
   * Constructor
   * @param http
   * @param eventBus
   * @param appServiceHelper
   * @param fileDownloadService
   */
  constructor(private http: HttpClient,
    private eventBus: EventBusService,
    private appServiceHelper: AppServiceHelperService,
    private fileDownloadService: FileDownloadService) {
    super();

    this.hideUserAcdNameColumn = environment.UseMsal || environment.UseOkta;

    this.onEvents.push(this.eventBus.subscribeEvent("settingsUserManagementaddNewRow", () => {
      this.showAsaDialog = true;
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('settingsUserManagementExportLogins', () => {
      const downloadUrl = this.appServiceHelper.getServiceEndpoint('pollux.settings.api', 'user/export/login');
      this.fileDownloadService.initiateDownload(downloadUrl, null, false);
    }));
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.loadData();
  }

  /**
   * Load data
   */
  loadData() {
    const users$ = this.http.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.settings.api', 'user'));
    const businessUnits$ = this.http.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.settings.api', 'user/businessUnits'));
    const roles$ = this.http.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.settings.api', 'user/roles'));
    const businessLineNr$ = this.http.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.api', '/reflist/pollux?reflistName=ref_valuation_businesslinenr'));
    const sectors$ = this.http.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.api', '/reflist/pollux?reflistName=ref_sector'));
    const regions$ = this.http.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.api', '/reflist/pollux?reflistName=ref_region'));
    const organizationalRoles$ = this.http.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.api', '/reflist/pollux?reflistName=ref_organizational_role'));

    this.loading = true;
    forkJoin([
      users$,
      businessUnits$,
      roles$,
      businessLineNr$,
      sectors$,
      regions$,
      organizationalRoles$
    ])
      .pipe(
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (result: any[]) => {
          const users = result[0];
          const bus = result[1];
          const roles = result[2];
          let businessLineNr = result[3];
          let sectors = result[4];
          let regions = result[5];
          let organizationalRoles = result[6];

          this.users = users;

          //fill "real" and "role" BUs depending on isRole property
          for (const user of users) {
            user.BusinessUnitsReal = user.BusinessUnits.filter(item => !item.isRole);
            user.BusinessUnitsRole = user.BusinessUnits.filter(item => item.isRole);
            user.BusinessUnits = [];

            for (const role of user.RoleAssignments) {
              role.oid = role.roleId;
              //we need name property for multiselect in html: valuePrimitive = false
              role.name = role.roleDescriptionLocalized;
            }

            for (const region of user.RegionsAssignment) {
              region.oid = region.regionOid;
              //we need description property for multiselect in html: valuePrimitive = false
              region.description = region.regionDescription;
            }
          }

          const busReal = bus.filter(item => !item.isRole);
          const busRole = bus.filter(item => item.isRole);

          const descProperty = "description";
          businessLineNr = businessLineNr.map(item => ({
            oid: item.oid,
            description: item[descProperty]
          }));

          sectors = sectors.map(item => ({
            oid: item.oid,
            description: item[descProperty]
          }));

          regions = regions.map(item => ({
            oid: item.oid,
            description: item[descProperty]
          }));

          organizationalRoles = organizationalRoles.map(item => ({
            oid: item.oid,
            description: item[descProperty]
          }));


          this.availableBUsReal = busReal;
          this.availableBUsRole = busRole;
          this.availableRoles = roles;
          this.availableBusinessLineNr = businessLineNr;
          this.availableSectors = sectors;
          this.availableRegions = regions.filter(x => x.description == 'EMEA' || x.description == 'APAC' || x.description == 'AMERICAS' || x.description == 'GLOBAL');
          this.availableOrganizationalRoles = organizationalRoles;
        },
        error: (error) => {
          console.error(error);
        }
      });
  }

  /**
    * Return description for grid dropdown items
    */
  public displayDropdownDescription(refTable, oid) {
    return refTable.find(r => r.oid === oid)?.description || '';
  }

  /**
   * The ASA dialog was cancelled
   */
  public asaCancel(): void {
    this.showAsaDialog = false;
  }

  /**
    * Grid handler for adding new row
    */
  public asaAddUser(user: any): void {
    setTimeout(() => { //Give the dialog time to close
      this.showAsaDialog = false;

      this.closeEditor(this.kendoGrid);
      // define all editable fields validators and default values
      //user: Null is manual, not null is from ASA. Preset the values

      if (user) {
        this.formGroup = new UntypedFormGroup({
          OID: new UntypedFormControl(-1),
          User_Lastname: new UntypedFormControl(user.User_Lastname),
          User_Firstname: new UntypedFormControl(user.User_Firstname),
          User_Description: new UntypedFormControl(user.User_Description),
          RTNumber: new UntypedFormControl(""),
          UserAcdName: new UntypedFormControl(user.UserAcdName),
          User_Email: new UntypedFormControl(user.User_Email),
          User_MailAddress: new UntypedFormControl(user.User_MailAddress),
          User_Active: new UntypedFormControl(true),
          BusinessUnitsReal: new UntypedFormControl([]),
          BusinessUnitsRole: new UntypedFormControl([]),
          RoleAssignments: new UntypedFormControl([]),
          BusinessLineNrId: new UntypedFormControl(0),
          SectorId: new UntypedFormControl(0),
          RegionsAssignment: new UntypedFormControl([]),
          OrganizationalRoleId: new UntypedFormControl(0)
        });
      }
      else {
        this.formGroup = new UntypedFormGroup({
          OID: new UntypedFormControl(-1),
          User_Lastname: new UntypedFormControl(""),
          User_Firstname: new UntypedFormControl(""),
          User_Description: new UntypedFormControl(""),
          RTNumber: new UntypedFormControl(""),
          UserAcdName: new UntypedFormControl(""),
          User_Email: new UntypedFormControl(""),
          User_MailAddress: new UntypedFormControl(""),
          User_Active: new UntypedFormControl(true),
          BusinessUnitsReal: new UntypedFormControl([]),
          BusinessUnitsRole: new UntypedFormControl([]),
          RoleAssignments: new UntypedFormControl([]),
          BusinessLineNrId: new UntypedFormControl(0),
          SectorId: new UntypedFormControl(0),
          RegionsAssignment: new UntypedFormControl([]),
          OrganizationalRoleId: new UntypedFormControl(0)
        });
      }

      // show the new row editor, with the `FormGroup` build above
      this.kendoGrid.addRow(this.formGroup);
    }, 100);
  }

  /**
    * Grid handler for editing row
    */
  public editHandler(args: EditEvent): void {
    // define all editable fields validators and default values
    const { dataItem } = args;
    this.closeEditor(args.sender);
    this.formGroup = new UntypedFormGroup({
      OID: new UntypedFormControl(dataItem.OID),
      User_Lastname: new UntypedFormControl(dataItem.User_Lastname),
      User_Firstname: new UntypedFormControl(dataItem.User_Firstname),
      User_Description: new UntypedFormControl(dataItem.User_Description),
      RTNumber: new UntypedFormControl(dataItem.RTNumber),
      UserAcdName: new UntypedFormControl(dataItem.UserAcdName),
      User_Email: new UntypedFormControl(dataItem.User_Email),
      User_MailAddress: new UntypedFormControl(dataItem.User_MailAddress),
      User_Active: new UntypedFormControl(dataItem.User_Active),
      BusinessUnitsReal: new UntypedFormControl(dataItem.BusinessUnitsReal),
      BusinessUnitsRole: new UntypedFormControl(dataItem.BusinessUnitsRole),
      RoleAssignments: new UntypedFormControl(dataItem.RoleAssignments),
      BusinessLineNrId: new UntypedFormControl(dataItem.BusinessLineNrId),
      SectorId: new UntypedFormControl(dataItem.SectorId),
      RegionsAssignment: new UntypedFormControl(dataItem.RegionsAssignment),
      OrganizationalRoleId: new UntypedFormControl(dataItem.OrganizationalRoleId)
    });

    this.editedRowIndex = args.rowIndex;
    // put the row in edit mode, with the `FormGroup` build above
    args.sender.editRow(args.rowIndex, this.formGroup);
  }

  /**
    * Close the editor for the given row
    */
  public cancelHandler(args: CancelEvent): void {
    this.closeEditor(args.sender, args.rowIndex);
  }

  /**
    * Close the editor for the given row
    */
  public async saveHandler({ sender, rowIndex, formGroup, isNew }: SaveEvent): Promise<void> {
    const user = formGroup.value;
    const success = await this.saveUser(user);
    if (success) {
      sender.closeRow(rowIndex);
    }
  }

  /**
    * Close grid editor
    */
  private closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  /**
    * Save user
    */
  private async saveUser(userDto: any): Promise<boolean> {
    //let's work on copy
    const user = ObjectUtil.deepCopy(userDto);
    //copy items from "real" and "roile" BUs to "normal" BU property
    user.BusinessUnits = user.BusinessUnitsReal.concat(user.BusinessUnitsRole);
    //empty "real" and "role" arrays, so they are not send to server (we have vl.loadData after saving)
    delete user.BusinessUnitsReal;
    delete user.BusinessUnitsRole;
    //fix role assignments (set role into roleId instead only to oid for added roles)
    user.RoleAssignments.filter((role) => !role.roleId).forEach((role) => { role.roleId = role.oid });

    const showDialogForMessage = (messageTranslationKey: string) => {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, messageTranslationKey);
    }

    const checkIfDuplicate = (message: string) => {
      if (message?.toLowerCase().indexOf('duplicate') >= 0) {
        showDialogForMessage('userManagementDuplicateEntry');
      }
    };

    //save user
    const saveUrl = this.appServiceHelper.getServiceEndpoint('pollux.settings.api', 'user');
    this.loading = true;
    try {
      const result = await RxjsHelper.toPromise(this.http.post<any>(saveUrl, user));
      if (result.m_Item1) {
        this.loadData();
        showDialogForMessage('userManagementApplyOnReload');
        return true;
      } else {
        checkIfDuplicate(result.m_Item2);
        return false;
      }
    } catch (error) {
      checkIfDuplicate(JSON.stringify(error));
      return false;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Adds a handler
   */
  public addHandler() {
    //Nothing
  }
}
